import '../../css/public/public.scss';
import instantsearch from 'instantsearch.js';
import 'instantsearch.css/themes/reset.css';

import { initTypesense } from '../shared/client';
import { initCart } from './modules/cart';
import { createAnalyticsMiddleware } from './modules/analytics';
import { setupRouting, setupSuggestionsRouting } from './modules/routing';
import { scrollToSnapfindAppTop } from './modules/facets';
import { getPromotionForQuery, renderPromotion } from './modules/promotions';
import { buildWidgets } from './modules/widgets';
import { setupSuggestionsUI } from './modules/suggestions';
import { initShortcuts } from './modules/shortcuts';

document.addEventListener('DOMContentLoaded', () => {
    // Initialize AJAX Add to Cart (delegated listener)
    initCart();
    const globalConfig = window.snapFindPublic;
    if (!globalConfig) return;

    // Initialize Keyboard Shortcuts
    initShortcuts();

    const apps = document.querySelectorAll('.snapfind-app');
    const groupedApps = {};

    apps.forEach((app, idx) => {
        const pt = app.dataset.postType || 'product';
        const isSuggestionsGroup = app.classList.contains('type-suggestions');
        const groupKey = isSuggestionsGroup ? `suggestions:${pt}:${idx}` : `standard:${pt}`;

        if (!groupedApps[groupKey]) {
            groupedApps[groupKey] = {
                postType: pt,
                apps: [],
                isSuggestionsGroup,
            };
        }
        groupedApps[groupKey].apps.push(app);
    });

    const nonSuggestionsGroupCount = Object.values(groupedApps).filter(
        (group) => !group.isSuggestionsGroup
    ).length;

    const promotions = Array.isArray(globalConfig.frontend?.promotions)
        ? globalConfig.frontend.promotions
        : [];

    // Synchronous URL flushers (one per routed group). InstantSearch throttles
    // its history writes (writeDelay 400ms), so a quick "click page N -> click a
    // product" sequence navigates away before the page change is persisted to the
    // URL. These flushers write the current routing state to the URL immediately
    // when a result link is clicked, so the back button restores pagination.
    const routingFlushers = [];
    const bodyScrollLocks = new Set();
    const setBodyScrollLock = (key, isLocked) => {
        if (isLocked) {
            bodyScrollLocks.add(key);
        } else {
            bodyScrollLocks.delete(key);
        }

        const active = bodyScrollLocks.size > 0;
        document.body.style.overflow = active ? 'hidden' : '';
    };

    const dispatchSnapFindEvent = (eventName, detail) => {
        document.dispatchEvent(new CustomEvent(eventName, { detail }));
    };

    const runAfterNextPaint = (callback) => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(callback);
        });
    };

    Object.values(groupedApps).forEach((group) => {
        const { postType, apps: appsInGroup, isSuggestionsGroup } = group;
        const config = globalConfig;
        if (!config) return;

        const typesenseAdapter = initTypesense(postType);
        if (!typesenseAdapter) return;

        const hasUpcField = (config.schema[postType] || []).some((f) => f.field_slug === 'upc');
        let activeUpcFilter = null;
        const indexName = `${config.server.collection_prefix}${postType}`;

        let previousUiState = null;
        let updatedEventFrame = null;
        let pendingUpdatedDetail = null;

        const scheduleUpdatedEvent = (detail) => {
            pendingUpdatedDetail = detail;
            if (updatedEventFrame !== null) return;

            updatedEventFrame = 1;
            runAfterNextPaint(() => {
                updatedEventFrame = null;
                if (!pendingUpdatedDetail) return;
                dispatchSnapFindEvent('snapfind:updated', pendingUpdatedDetail);
                pendingUpdatedDetail = null;
            });
        };

        const initialUrlQuery = new URLSearchParams(window.location.search).get('q') || '';

        const routingConfig = isSuggestionsGroup
            ? setupSuggestionsRouting(indexName, () =>
                  appsInGroup.some((app) => app.classList.contains('snaf-suggestions-open'))
              )
            : nonSuggestionsGroupCount === 1
              ? setupRouting(indexName)
              : false;

        const search = instantsearch({
            indexName,
            searchClient: typesenseAdapter.searchClient,
            ...(routingConfig === false && {
                initialUiState: {
                    [indexName]: {
                        query: initialUrlQuery,
                    },
                },
            }),
            onStateChange({ uiState, setUiState }) {
                if (previousUiState !== null) {
                    const prevIndexState = previousUiState[indexName] || {};
                    const nextIndexState = uiState[indexName] || {};

                    const pageChanged = prevIndexState.page !== nextIndexState.page;

                    const getFacets = (state) => {
                        const { query, configure, page, ...facets } = state;
                        return JSON.stringify(facets);
                    };
                    const facetsChanged = getFacets(prevIndexState) !== getFacets(nextIndexState);

                    const autoScrollPagination = config.frontend.autoscroll_pagination !== 'no';
                    const autoScrollFacets = config.frontend.autoscroll_facets !== 'no';

                    if (
                        (pageChanged && autoScrollPagination) ||
                        (facetsChanged && autoScrollFacets)
                    ) {
                        scrollToSnapfindAppTop(50, 950);
                    }
                }

                previousUiState = uiState;
                setUiState(uiState);
            },
            routing: routingConfig,
        });

        search.use(createAnalyticsMiddleware(postType));

        const SEARCH_DEBOUNCE_MS = 200;
        let searchDebounceTimer = null;
        const debounceQueryHook = (query, doSearch) => {
            if (hasUpcField) {
                const trimmed = String(query || '').trim();
                const isUpc = /^\d{12,13}$/.test(trimmed);
                const desiredFilter = isUpc ? `upc:=${trimmed}` : null;

                if (desiredFilter !== activeUpcFilter) {
                    activeUpcFilter = desiredFilter;
                    const base = typesenseAdapter._snapfindBaseConfig;
                    typesenseAdapter.updateConfiguration({
                        server: base.server,
                        additionalSearchParameters: desiredFilter
                            ? { ...base.additionalSearchParameters, filter_by: desiredFilter }
                            : { ...base.additionalSearchParameters },
                    });
                }
            }

            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = setTimeout(() => doSearch(query), SEARCH_DEBOUNCE_MS);
        };

        const widgets = buildWidgets(
            postType,
            appsInGroup,
            config,
            isSuggestionsGroup,
            debounceQueryHook
        );

        // Setup Mobile Filter Drawer Logic (Sidebar)
        appsInGroup.forEach((app) => {
            const toggleBtn = app.querySelector('.snaf-mobile-filter-toggle');
            const closeBtn = app.querySelector('.snaf-mobile-filter-close');
            const overlay = app.querySelector('.snapfind-overlay');
            const sidebar = app.querySelector('.snapfind-sidebar');

            const toggleDrawer = () => {
                if (sidebar) sidebar.classList.toggle('is-open');
                if (overlay) overlay.classList.toggle('is-visible');
                const isOpen = !!(sidebar && sidebar.classList.contains('is-open'));
                setBodyScrollLock(`mobile-drawer:${postType}`, isOpen);
            };

            if (toggleBtn) toggleBtn.addEventListener('click', toggleDrawer);
            if (closeBtn) closeBtn.addEventListener('click', toggleDrawer);
            if (overlay) overlay.addEventListener('click', toggleDrawer);

            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                    if (window.innerWidth > 860) {
                        if (sidebar && sidebar.classList.contains('is-open')) {
                            toggleDrawer();
                        }
                    }
                }, 100);
            });
        });

        if (hasUpcField && !isSuggestionsGroup) {
            const urlParams = new URLSearchParams(window.location.search);
            const initialQuery = urlParams.get('q') || '';
            const trimmed = String(initialQuery).trim();
            if (/^\d{12,13}$/.test(trimmed)) {
                activeUpcFilter = `upc:=${trimmed}`;
                const base = typesenseAdapter._snapfindBaseConfig;
                typesenseAdapter.updateConfiguration({
                    server: base.server,
                    additionalSearchParameters: {
                        ...base.additionalSearchParameters,
                        filter_by: activeUpcFilter,
                    },
                });
            }
        }

        search.addWidgets(widgets);
        search.start();

        if (routingConfig) {
            routingFlushers.push((link) => {
                const container = link.closest('.snapfind-app');
                if (!container) return;

                const belongs =
                    appsInGroup.includes(container) ||
                    (isSuggestionsGroup && container.classList.contains('snaf-injected-app'));
                if (!belongs) return;

                // For suggestions, only sync the URL while the takeover is open
                // (matches the conditional routing write guard).
                if (isSuggestionsGroup) {
                    const isOpen = appsInGroup.some((app) =>
                        app.classList.contains('snaf-suggestions-open')
                    );
                    if (!isOpen) return;
                }

                try {
                    const route = routingConfig.stateMapping.stateToRoute(search.getUiState());
                    const url = routingConfig.router.createURL(route);
                    if (url && url !== window.location.href) {
                        window.history.replaceState(null, '', url);
                    }
                } catch (err) {
                    /* no-op: never block navigation on a URL sync failure */
                }
            });
        }

        runAfterNextPaint(() => {
            dispatchSnapFindEvent('snapfind:init', {
                postType,
                indexName,
                isSuggestionsGroup,
                appCount: appsInGroup.length,
                timestamp: Date.now(),
            });
        });

        // Cache the promo banner element per app now, while it is still inside
        // the app. The suggestions takeover moves the dropdown (which contains
        // the banner) out of the app, so re-querying `app.querySelector` later
        // would return null. DOM references survive the move, just like the
        // InstantSearch widget bindings do.
        const promoBannerByApp = new Map();
        appsInGroup.forEach((app) => {
            const banner = app.querySelector('.snaf-promo-banner');
            if (banner) promoBannerByApp.set(app, banner);
        });

        search.on('render', () => {
            const currentQuery =
                search.helper && search.helper.state ? search.helper.state.query || '' : '';
            const promotion = getPromotionForQuery(currentQuery, promotions);

            appsInGroup.forEach((app) => {
                renderPromotion(promoBannerByApp.get(app), promotion);

                const btn = app.querySelector('.ais-InfiniteHits-loadMore');
                if (!btn) return;

                const renderState = search.renderState[indexName];
                const isLastPage =
                    renderState && renderState.infiniteHits
                        ? renderState.infiniteHits.isLastPage
                        : false;

                btn.innerText = isLastPage ? "That's all" : 'Load More';

                if (!btn.dataset.snafInited) {
                    btn.dataset.snafInited = 'true';
                    btn.addEventListener('click', () => {
                        if (!btn.classList.contains('ais-InfiniteHits-loadMore--disabled')) {
                            btn.innerText = 'Loading...';
                        }
                    });
                }
            });

            const helperState = search.helper && search.helper.state ? search.helper.state : null;
            const results =
                search.helper && search.helper.lastResults ? search.helper.lastResults : null;

            scheduleUpdatedEvent({
                postType,
                indexName,
                isSuggestionsGroup,
                appCount: appsInGroup.length,
                query: helperState && helperState.query ? helperState.query : '',
                page:
                    helperState && typeof helperState.page === 'number' ? helperState.page + 1 : 1,
                nbHits: results && typeof results.nbHits === 'number' ? results.nbHits : 0,
                timestamp: Date.now(),
            });
        });

        appsInGroup.forEach((app, index) => {
            setupSuggestionsUI(app, search, index === 0);
        });
    });

    // Flush the throttled routing state into the URL synchronously before the
    // browser navigates away via a result link, so pagination/filters/query are
    // all restored when the user hits the back button.
    if (routingFlushers.length > 0) {
        document.addEventListener(
            'click',
            (e) => {
                if (e.defaultPrevented) return;
                // Ignore non-primary clicks and new-tab/new-window intents.
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

                const target = e.target;
                const link = target && target.closest ? target.closest('a[href]') : null;
                if (!link) return;

                const href = link.getAttribute('href');
                if (!href || href.startsWith('#')) return;
                if (link.target && link.target !== '_self') return;
                if (link.hasAttribute('download')) return;

                routingFlushers.forEach((flush) => flush(link));
            },
            true
        );
    }
});
